import type { Session } from "@supabase/supabase-js";
import { getSupabase } from "./supabaseClient.js";

type SessionListener = (session: Session | null) => void;

let currentSession: Session | null = null;
const listeners = new Set<SessionListener>();
let isInitialized = false;

function setSession(nextSession: Session | null): void {
  currentSession = nextSession;
  for (const listener of listeners) listener(nextSession);
}

export function getCurrentSession(): Session | null {
  return currentSession;
}

export function subscribeToSession(listener: SessionListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function clearAuthHash(): void {
  if (typeof window === "undefined") return;
  history.replaceState(null, "", window.location.pathname + window.location.search);
}

export function consumeAuthHashError(): string | null {
  if (typeof window === "undefined") return null;
  const raw = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  if (!raw.includes("error=")) return null;
  const params = new URLSearchParams(raw);
  const description = params.get("error_description") ?? params.get("error") ?? "Auth failed";
  clearAuthHash();
  return description.replaceAll("+", " ");
}

export async function initSessionStore(): Promise<Session | null> {
  if (isInitialized) return currentSession;
  isInitialized = true;

  const supabase = getSupabase();
  const { data } = await supabase.auth.getSession();
  setSession(data.session);

  supabase.auth.onAuthStateChange((_event, session) => {
    setSession(session);
  });

  if (
    typeof window !== "undefined" &&
    (window.location.hash.includes("access_token=") ||
      window.location.hash.includes("refresh_token="))
  ) {
    clearAuthHash();
  }

  return currentSession;
}

export async function signInWithMagicLink(
  email: string
): Promise<{ ok: true } | { ok: false; error: string }> {
  return requestEmailCode(email);
}

export async function requestEmailCode(
  email: string
): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const { error } = await getSupabase().auth.signInWithOtp({
      email,
      options: { shouldCreateUser: true },
    });
    if (error) return { ok: false, error: mapAuthError(error.message) };
  } catch (error) {
    return { ok: false, error: mapAuthError(toErrorMessage(error)) };
  }
  return { ok: true };
}

export async function verifyEmailCode(
  email: string,
  code: string
): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const { error } = await getSupabase().auth.verifyOtp({
      email,
      token: code,
      type: "email",
    });
    if (error) return { ok: false, error: mapAuthError(error.message) };
  } catch (error) {
    return { ok: false, error: mapAuthError(toErrorMessage(error)) };
  }
  return { ok: true };
}

export async function signOut(): Promise<void> {
  await getSupabase().auth.signOut();
}

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

function mapAuthError(message: string): string {
  const normalized = message.toLowerCase();
  if (
    normalized.includes("failed to fetch") ||
    normalized.includes("fetch failed") ||
    normalized.includes("networkerror")
  ) {
    return "Can't reach Supabase. Check your connection, ad-block/privacy extensions, and VITE_SUPABASE_URL.";
  }
  if (normalized.includes("rate limit")) {
    return "Too many sign-in emails sent. Wait a minute and try again.";
  }
  return message;
}
